import { useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { WebSearchCitation } from "@shared/schema";

interface CitationsDisplayProps {
  citations: WebSearchCitation[];
  actionButtons?: ReactNode;
}

export function CitationsDisplay({ citations, actionButtons }: CitationsDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!citations || citations.length === 0) {
    return null;
  }

  return (
    <div className="mt-2" data-testid="citations-container">
      <div className="flex items-center gap-1 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-muted-foreground hover-elevate"
          data-testid="button-toggle-citations"
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
          <span className="text-sm">
            {citations.length} {citations.length === 1 ? "source" : "sources"}
          </span>
        </Button>
        {actionButtons}
      </div>

      {isExpanded && (
        <div className="mt-2 space-y-2" data-testid="citations-list">
          {citations.map((citation, index) => (
            <Card
              key={index}
              className="p-3 hover-elevate"
              data-testid={`citation-${index}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0">
                      {index + 1}
                    </span>
                    {citation.url_citation.title ? (
                      <h4 className="font-medium text-sm truncate">
                        {citation.url_citation.title}
                      </h4>
                    ) : (
                      <span className="text-sm text-muted-foreground truncate">
                        Untitled Source
                      </span>
                    )}
                  </div>
                  
                  {citation.url_citation.content && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                      {citation.url_citation.content}
                    </p>
                  )}
                  
                  <a
                    href={citation.url_citation.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                    data-testid={`citation-link-${index}`}
                  >
                    <span className="truncate max-w-[300px]">
                      {citation.url_citation.url}
                    </span>
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
